import {Location} from '@angular/common';
import {HttpErrorResponse} from '@angular/common/http';
import {Component, EventEmitter, Inject, Input, LOCALE_ID, OnDestroy, OnInit, Output} from '@angular/core';
import {UntypedFormBuilder, UntypedFormGroup} from '@angular/forms';
import {ActivatedRoute, Router} from '@angular/router';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {SpinnerService} from '~shared/components/buttons/spinner/spinner.service';
import {TablesTranslations} from '~shared/components/tables/tables-translations.service';
import {NotificationType} from '~core/enum/notification.enum';
import {AuthenticatedUser, Club, User} from '~core/models';
import {ClubService, DefaultNotificationService, NavigationService, StorageService, UserManagementService, UserService,} from '~core/services';
import {SharedTranslationsService} from '~shared/translations';


/**
 * Component responsible for user administration.
 */
@UntilDestroy()
@Component({
  selector: 'app-club-assignment',
  templateUrl: './club-assignment.component.html',
})
export class ClubAssignmentComponent implements OnInit, OnDestroy {
  public listOfUserClubs: Array<Club> = [];
  public userForm: UntypedFormGroup;
  public tableData: Array<any> = [];
  public filteredTableData: Array<any> = [];
  public columns: Array<string> = [];
  public keys: Array<Array<string>> = [];
  public spinnerName: string = 'clubAssignmentSubmit';
  public i18nPlaceholder: string;
  @Input() public authenticatedUser: AuthenticatedUser;
  @Input() public listOfAllClubs: Array<Club>;
  @Input() public user: User;
  @Output() public submitted: EventEmitter<any> = new EventEmitter<any>(null);

  constructor(
    public notificationService: DefaultNotificationService,
    public navigationService: NavigationService,
    public userService: UserService,
    public userModalService: UserManagementService,
    public router: Router,
    public clubService: ClubService,
    public alertTranslations: SharedTranslationsService,
    public location: Location,
    public formBuilder: UntypedFormBuilder,
    public storage: StorageService,
    public route: ActivatedRoute,
    private spinnerService: SpinnerService,
    private tablesTranslations: TablesTranslations,
    @Inject(LOCALE_ID) public locale: string
  ) {
    this.i18nPlaceholder = $localize`:@@userProfile.formField.filterByClubName:Filter by club name`;

  }

  /**
   * On init load the user fetched from the url param.
   */
  public ngOnInit(): void {
    this.createForm();
    this.initialSetup();
    this.fillSelectData();
  }

  /**
   * On component destroy, close the modal.
   */
  public ngOnDestroy(): void {
    this.userModalService.toggleModal(false);
  }

  /**
   * Get the requested user.
   */
  public initialSetup(): void {
    this.columns.push(this.tablesTranslations.i18nClubName);
    this.keys.push(['name']);
    this.listOfUserClubs = this.user.clubs;
  }

  /**
   * Creates user form.
   */
  public createForm(): void {
    this.userForm = this.formBuilder.group({
      clubs: [null]
    });
  }

  /**
   * Fills the select data while adding the selected property
   */
  public fillSelectData() {
    if (this.authenticatedUser.isAdmin) {
      for (const club of this.listOfAllClubs) {
        const obj = {
          id: club.id,
          name: club.name,
          selected: this.checkIfActive(club)
        };
        this.tableData.push(obj);
      }
      this.filteredTableData = this.tableData;
    }
  }

  /**
   * Checks if attributes are added on the customer
   */
  public checkIfActive(club: any): any {
    if (this.listOfUserClubs) {
      return this.listOfUserClubs.some(el => el.id === club.id);
    }
  }

  /**
   * Updates the attribute on customer when clicking the toggle button
   */
  public changeUserClubs(event: any): any {
    const userClubs = [];
    for (const data of event) {
      if (data.selected) {
        userClubs.push(`/api/clubs/${data.id}`);
      }
    }

    this.userForm.patchValue({
      clubs: userClubs
    });
  }

  /**
   * Submit the user club-overview form
   */
  public submitUserClubs(): void {
    this.changeUserClubs(this.tableData);
    if (this.userForm.get('clubs').value?.length === 0) {
      this.notificationService.showNotification({
        type: NotificationType.ALERT,
        message: this.alertTranslations.i18nErrorAtLeastOne
      });
    } else {
      this.spinnerService.show(this.spinnerName);
      this.userService.update(this.userForm.value, this.user.id).pipe(untilDestroyed(this))
        .subscribe({
          next: (res: User) => {
            this.spinnerService.hide(this.spinnerName);
            this.submitted.emit('submit');
            this.notificationService.showNotification({
              type: NotificationType.SUCCESS,
              message: this.alertTranslations.i18nUserUpdated
            });
          },
          error: (error: HttpErrorResponse) => {
            if (error.error !== null) {
              this.spinnerService.hide(this.spinnerName);
              this.notificationService.showNotification({
                type: NotificationType.ALERT,
                message: error.error['hydra:description']
              });
            }
          }
        });
    }
  }

  setSelected(data: { id: any }): void {
    const index = this.tableData.findIndex(el => el.id === data.id);
    this.tableData[index].selected = !this.tableData[index].selected;
  }

  filterTable(query: string): void {
    this.filteredTableData = this.tableData;
    this.filteredTableData = this.filteredTableData.filter(el => el.name.toLowerCase().includes(query.toLowerCase()));
  }

}
